import { Component } from 'react';
import { connect } from 'react-redux';
import { Dropdown } from 'react-bootstrap';

import { Brand } from './Brand';
import ProfileIconDropdownMenu from './ProfileIconDropdownMenu';
import NavbarItem from './NavbarItem';
import NavbarSignOutItem from './NavbarSignOutItem';
import {
    IAdminDashboardData,
    ArchivedProjectBrowserModel,
    ConfigurationType,
    User,
    VersionType,
} from '../../store/types';
import {
    RootState,
    getAdminDashboardData,
    getFrontConfiguration,
    getLatestVersion,
} from '../../store';
import {
    ArchiveIcon,
    EmailIcon,
    EyeIcon,
    GearIcon,
    NavbarDashboardIcon,
    ProjectsIcon,
    QuestionCircleIcon,
    ResearchAreasIcon,
    SupervisorsIcon,
} from '../shared/icons/icons';
import { ILocalization } from '../../localization/definitions/types';
import { UserRoles } from '../../utils/utils';
import { cognitoSignOut } from '../../services/authentication';
import SusyAvatar, { SuSyAvatarSizes } from '../shared/susyAvatar/SusyAvatar';
import NavbarVersionItem from './NavbarVersionItem';
import { DataProtectionNavbarItem } from './DataProtectionNavbarItem';
import LanguageSwitcher from '../localization/LanguageSwitcher';
import { unauthenticatedPaths } from '../../router/constants/route-paths';
import { getAllArchiveProjects } from '../../store/actions/archive/ArchivedProjectActions';
import { CountDisplay } from '../shared/CountDisplay';
import {
    AdminSupervisorsActiveTab,
    AdminTabs,
    AdminProjectsTabs,
    GeneralSettingsTabs,
    HelpTabs,
} from '../admin/adminTabDefinitions';
import { SupervisorSupervisionsTabs } from '../supervision/enums';
import { UserDashboardTabNames } from '../../components/thesisManagement/UserDashboard';

import './Navbar.css';
import { sharedActiveNavItems } from '../../router/constants/active-nav-items';
import ContactAdminForm from './contactAdminForm/ContactAdminForm';

interface NavbarProps {
    activeNavItem: string;
    currentUser: User | null;
    localization: ILocalization;
    getFrontConfiguration: () => Promise<void>;
    getLatestVersion: () => Promise<void>;
    getAllArchiveProjects: () => Promise<void>;
    getAdminDashboardData: () => Promise<void>;
    configuration: ConfigurationType;
    latestVersion: VersionType;
    adminDashboardData: IAdminDashboardData;
    archivedProjects: ArchivedProjectBrowserModel[];
}

interface NavbarState {
    showMiniNavbar: boolean;
    showContactModal: boolean;
}

class Navbar extends Component<NavbarProps, NavbarState> {
    constructor(props: NavbarProps) {
        super(props);
        this.state = {
            showMiniNavbar: false,
            showContactModal: false,
        };
    }

    componentDidMount = async () => {
        if (!this.props?.latestVersion?.version) {
            await this.props.getLatestVersion();
        }
        await this.props.getFrontConfiguration();

        if (this.props.currentUser?.userRole.key === UserRoles.admin) {
            this.props.getAllArchiveProjects();

            this.props.getAdminDashboardData();
        }
    };

    handleSignOut = async () => {
        await cognitoSignOut();
        window.location.href = unauthenticatedPaths.signIn;
    };

    getAdminProjectsLabel = () => {
        const localization =
            this.props.localization.localizationStrings.susy.navbar
                .lecturerNavbar;

        const projectsCount = this.props.adminDashboardData.projects.length;

        return (
            <div className={'d-flex align-items-center'}>
                <p className={'mb-0'}>{localization.projectsNavItem}</p>
                <CountDisplay
                    counter={projectsCount}
                    prefix={'('}
                    suffix={')'}
                    classes={'ml-2'}
                />
            </div>
        );
    };

    getAdminArchivedProjectsLabel = () => {
        const localization =
            this.props.localization.localizationStrings.susy.navbar
                .lecturerNavbar;

        const projectsCount = this.props.archivedProjects.length;

        return (
            <div className={'d-flex align-items-center'}>
                <p className={'mb-0'}>{localization.archive}</p>
                <CountDisplay
                    counter={projectsCount}
                    prefix={'('}
                    suffix={')'}
                    classes={'ml-2'}
                />
            </div>
        );
    };

    openContactForm = () => {
        this.setState({
            showContactModal: true,
        });
    };

    render() {
        const navbarLocalization =
            this.props.localization.localizationStrings.susy.navbar;
        return (
            <nav
                className='navbar sticky-top navbar-vertical fixed-left navbar-expand-md navbar-light'
                id='sidebar'
            >
                <div className='container-fluid'>
                    {/* Toggler */}
                    <button
                        className='navbar-toggler'
                        type='button'
                        aria-controls='sidebarCollapse'
                        aria-expanded='false'
                        aria-label='Toggle navigation'
                        onClick={() => {
                            this.setState({
                                showMiniNavbar: !this.state.showMiniNavbar,
                            });
                        }}
                    >
                        <span className='navbar-toggler-icon'></span>
                    </button>
                    <Brand
                        role={
                            this.props.currentUser?.userRole.key ||
                            UserRoles.user
                        }
                    />
                    {/* Profile picture */}
                    <div className='navbar-user d-md-none'>
                        <Dropdown>
                            <Dropdown.Toggle className='navbar-toggler pt-0'>
                                <SusyAvatar
                                    size={SuSyAvatarSizes.MD}
                                    profilePictureId={
                                        this.props.currentUser?.profilePictureId
                                    }
                                />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className='super-colors'>
                                <Dropdown.Item>
                                    <ProfileIconDropdownMenu />
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    {/* <!-- Collapse Navbar --> */}
                    <div
                        className={`${
                            !this.state.showMiniNavbar ? 'collapse' : ''
                        } navbar-collapse susy-nav`}
                        id='sidebarCollapse'
                    >
                        {/* <!-- Navigation --> */}
                        <ul className='navbar-nav'>
                            {/* Student */}
                            <NavbarItem
                                icon={<NavbarDashboardIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .dashboardNavItem
                                }
                                link={`/user/dashboard/${UserDashboardTabNames.yourProjects}`}
                                userRole='User'
                                isActive={
                                    this.props.activeNavItem === 'dashboard'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />
                            <NavbarItem
                                icon={<ResearchAreasIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .researchAreaNavItem
                                }
                                link='/user/researchAreas'
                                userRole='User'
                                isActive={
                                    this.props.activeNavItem === 'researchAreas'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />
                            <NavbarItem
                                icon={<ArchiveIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar.archive
                                }
                                link='/user/archive'
                                userRole='User'
                                isActive={
                                    this.props.activeNavItem === 'archive'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />

                            {/*Help*/}

                            <NavbarItem
                                icon={<QuestionCircleIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .helpNavItem
                                }
                                link={`/help/${HelpTabs.Tips}`}
                                userRole='User'
                                isActive={this.props.activeNavItem === 'help'}
                            />
                            {this.props.currentUser?.userRole.key ===
                                UserRoles.user && (
                                <hr className='w-100 p-0 m-0'></hr>
                            )}
                            <NavbarItem
                                icon={<EmailIcon color='#31417A' />}
                                label={
                                    navbarLocalization.contactAdminButton
                                        .buttonTextUser
                                }
                                link=''
                                userRole='User'
                                isActive={false}
                                onClick={() => this.openContactForm()}
                                dataTestId='contact_admin_button'
                            />

                            <NavbarItem
                                icon={''}
                                label=''
                                link=''
                                userRole='User'
                                isActive={false}
                                spacer={true}
                            />

                            {/* Supervisor */}
                            <NavbarItem
                                icon={<NavbarDashboardIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .dashboardNavItem
                                }
                                link='/supervisor/dashboard'
                                userRole='Supervisor'
                                isActive={
                                    this.props.activeNavItem === 'dashboard'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />
                            <NavbarItem
                                icon={<EyeIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .mySupervisionsNavItem
                                }
                                link={`/supervisor/supervisions/${SupervisorSupervisionsTabs.MySupervisions}`}
                                userRole='Supervisor'
                                isActive={
                                    this.props.activeNavItem === 'supervisions'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />
                            <NavbarItem
                                icon={<ResearchAreasIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .myResearchAreasNavItem
                                }
                                link='/supervisor/researchAreas'
                                userRole='Supervisor'
                                isActive={
                                    this.props.activeNavItem ===
                                    'supervisionResearchAreas'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />

                            <NavbarItem
                                icon={<ArchiveIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar.archive
                                }
                                link='/supervisor/archive'
                                userRole='Supervisor'
                                isActive={
                                    this.props.activeNavItem === 'archive'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />

                            <NavbarItem
                                icon={<QuestionCircleIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .helpNavItem
                                }
                                link={`/help/${HelpTabs.Tips}`}
                                userRole='Supervisor'
                                isActive={this.props.activeNavItem === 'help'}
                            />
                            {this.props.currentUser?.userRole.key ===
                                UserRoles.supervisor && (
                                <hr className='w-100 p-0 m-0'></hr>
                            )}
                            <NavbarItem
                                icon={<EmailIcon color='#31417A' />}
                                label={
                                    navbarLocalization.contactAdminButton
                                        .buttonTextSupervisor
                                }
                                link=''
                                userRole='Supervisor'
                                isActive={false}
                                onClick={() => this.openContactForm()}
                                dataTestId='contact_admin_button'
                            />
                            <NavbarItem
                                icon={''}
                                label=''
                                link=''
                                userRole='Supervisor'
                                isActive={false}
                                spacer={true}
                            />

                            {/* Admin */}
                            <NavbarItem
                                icon={<NavbarDashboardIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .dashboardNavItem
                                }
                                link='/admin/dashboard'
                                userRole='Admin'
                                isActive={
                                    this.props.activeNavItem ===
                                    sharedActiveNavItems.dashboard
                                }
                            />
                            <NavbarItem
                                icon={<ProjectsIcon />}
                                label={this.getAdminProjectsLabel()}
                                link={`/admin/projects/${AdminProjectsTabs.Projects}`}
                                userRole='Admin'
                                isActive={
                                    this.props.activeNavItem === 'adminProjects'
                                }
                            />
                            <NavbarItem
                                icon={<ArchiveIcon />}
                                label={this.getAdminArchivedProjectsLabel()}
                                link='/admin/archive'
                                userRole='Admin'
                                isActive={
                                    this.props.activeNavItem === 'adminArchive'
                                }
                            />
                            <NavbarItem
                                icon={<SupervisorsIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .supervisorsNavItem
                                }
                                link={`/admin/supervisors/${AdminSupervisorsActiveTab.internal}`}
                                userRole='Admin'
                                isActive={
                                    this.props.activeNavItem ===
                                    'adminSupervisors'
                                }
                                onClick={() => {
                                    this.setState({
                                        showMiniNavbar: false,
                                    });
                                }}
                            />
                            <NavbarItem
                                icon={<GearIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .settingsNavItem
                                }
                                link={`/admin/settings/${AdminTabs.GENERAL_SETTINGS_TAB}/${GeneralSettingsTabs.Susy}`}
                                userRole='Admin'
                                isActive={
                                    this.props.activeNavItem === 'adminSetup'
                                }
                            />
                            <NavbarItem
                                icon={<QuestionCircleIcon />}
                                label={
                                    navbarLocalization.lecturerNavbar
                                        .helpNavItem
                                }
                                link={`/help/${HelpTabs.Tips}`}
                                userRole='Admin'
                                isActive={this.props.activeNavItem === 'help'}
                            />

                            <NavbarItem
                                icon={''}
                                label=''
                                link=''
                                userRole='Admin'
                                isActive={false}
                                spacer={true}
                            />
                            {this.props.configuration.featureSwitch
                                .languageSwitcher && (
                                <div className={'ml-auto mr-auto mb-5'}>
                                    <LanguageSwitcher />
                                </div>
                            )}
                            <NavbarSignOutItem
                                handleSignOut={this.handleSignOut}
                            />
                            <hr className='w-100 p-0 m-0'></hr>
                            <div className='data-protection-version-wrapper'>
                                <DataProtectionNavbarItem
                                    localization={this.props.localization}
                                />
                                <NavbarVersionItem
                                    versionNumber={
                                        this.props.latestVersion.version
                                    }
                                    linkPrefix={
                                        this.props.currentUser?.userRole.key ??
                                        ''
                                    }
                                ></NavbarVersionItem>
                            </div>
                        </ul>
                        <ContactAdminForm
                            onHideModal={() =>
                                this.setState({ showContactModal: false })
                            }
                            showModal={this.state.showContactModal}
                        />
                        <div className='mt-auto'></div>
                    </div>
                </div>
            </nav>
        );
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        archivedProjects: state.archivedProjects,
        adminDashboardData: state.adminDashboardData,
        localization: state.localization,
        currentUser: state.auth.currentUser,
        configuration: state.configuration,
        latestVersion: state.version,
    };
};

export default connect(mapStateToProps, {
    getFrontConfiguration,
    getLatestVersion,
    getAllArchiveProjects,
    getAdminDashboardData,
})(Navbar);
